import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import api from '@/api/client'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { Plus, Pencil, Trash2 } from 'lucide-react'
import type { PaginatedResponse, ProductResponse } from '@/api/types'
import { HelpBanner } from '@/components/ui/help-banner'

export function ProductsPage() {
  const { t } = useTranslation(['tenants', 'common'])
  const [offset, setOffset] = useState(0)
  const [createOpen, setCreateOpen] = useState(false)
  const [editProduct, setEditProduct] = useState<ProductResponse | null>(null)
  const [deleteProduct, setDeleteProduct] = useState<ProductResponse | null>(null)
  const [newCode, setNewCode] = useState('')
  const [newName, setNewName] = useState('')
  const [editCode, setEditCode] = useState('')
  const [editName, setEditName] = useState('')
  const queryClient = useQueryClient()
  const limit = 20

  const { data, isLoading } = useQuery({
    queryKey: ['products', offset],
    queryFn: () => api.get<PaginatedResponse<ProductResponse>>(`/products?offset=${offset}&limit=${limit}`),
  })

  const createMutation = useMutation({
    mutationFn: (body: { code: string; name: string }) => api.post('/products', body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      setCreateOpen(false)
      setNewCode('')
      setNewName('')
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, ...body }: { id: number; code: string; name: string }) =>
      api.put(`/products/${id}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      setEditProduct(null)
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => api.delete(`/products/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['products'] })
      setDeleteProduct(null)
    },
  })

  const products = data?.data.data ?? []
  const total = data?.data.total ?? 0

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-semibold">{t('nav.products', { ns: 'common' })}</h2>
          <p className="text-sm text-muted-foreground">{t('products.subtitle')}</p>
        </div>
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger render={<Button />}>
            <Plus className="h-4 w-4 mr-2" />{t('products.newProduct')}
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>{t('products.createProduct')}</DialogTitle></DialogHeader>
            <form onSubmit={(e) => { e.preventDefault(); createMutation.mutate({ code: newCode, name: newName }) }} className="flex flex-col gap-4">
              <div>
                <Label htmlFor="product-code">{t('labels.code', { ns: 'common' })}</Label>
                <Input id="product-code" value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder={t('products.codePlaceholder')} required />
              </div>
              <div>
                <Label htmlFor="product-name">{t('labels.name', { ns: 'common' })}</Label>
                <Input id="product-name" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder={t('products.namePlaceholder')} required />
              </div>
              <Button type="submit" disabled={createMutation.isPending}>{t('buttons.create', { ns: 'common' })}</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <HelpBanner storageKey="products">
        <p>{t('products.help.line1')}</p>
        <p>{t('products.help.line2')}</p>
      </HelpBanner>

      {isLoading ? (
        <p className="text-muted-foreground">{t('loading', { ns: 'common' })}</p>
      ) : (
        <>
          <div className="border rounded-md">
            <table className="w-full text-sm">
              <thead><tr className="border-b bg-muted/50">
                <th className="text-left p-3 font-medium">{t('labels.id', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.code', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.name', { ns: 'common' })}</th>
                <th className="text-left p-3 font-medium">{t('labels.type', { ns: 'common' })}</th>
                <th className="text-right p-3 font-medium">{t('labels.actions', { ns: 'common' })}</th>
              </tr></thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="p-3 text-muted-foreground">{p.id}</td>
                    <td className="p-3 font-mono">{p.code}</td>
                    <td className="p-3">{p.name}</td>
                    <td className="p-3">{p.is_platform ? <Badge variant="secondary">Platform</Badge> : <Badge variant="outline">Bookmaker</Badge>}</td>
                    <td className="p-3 text-right">
                      <Button variant="ghost" size="sm" onClick={() => { setEditProduct(p); setEditCode(p.code); setEditName(p.name) }}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteProduct(p)}>
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
                {products.length === 0 && <tr><td colSpan={5} className="p-3 text-center text-muted-foreground">{t('products.empty')}</td></tr>}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between mt-4">
            <span className="text-sm text-muted-foreground">{t('labels.total', { ns: 'common' })}: {total}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}>{t('pagination.previous', { ns: 'common' })}</Button>
              <Button variant="outline" size="sm" disabled={offset + limit >= total} onClick={() => setOffset(offset + limit)}>{t('pagination.next', { ns: 'common' })}</Button>
            </div>
          </div>
        </>
      )}

      {/* Edit dialog */}
      <Dialog open={!!editProduct} onOpenChange={(open) => { if (!open) setEditProduct(null) }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('products.editProduct')}</DialogTitle></DialogHeader>
          <form onSubmit={(e) => { e.preventDefault(); if (editProduct) updateMutation.mutate({ id: editProduct.id, code: editCode, name: editName }) }} className="flex flex-col gap-4">
            <div>
              <Label htmlFor="edit-code">{t('labels.code', { ns: 'common' })}</Label>
              <Input id="edit-code" value={editCode} onChange={(e) => setEditCode(e.target.value)} required />
            </div>
            <div>
              <Label htmlFor="edit-name">{t('labels.name', { ns: 'common' })}</Label>
              <Input id="edit-name" value={editName} onChange={(e) => setEditName(e.target.value)} required />
            </div>
            <Button type="submit" disabled={updateMutation.isPending}>{t('buttons.save', { ns: 'common' })}</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteProduct} onOpenChange={(open) => { if (!open) setDeleteProduct(null) }}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('products.deleteProduct')}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {t('products.deleteConfirm', { name: deleteProduct?.name, code: deleteProduct?.code })}
          </p>
          <div className="flex justify-end gap-2 mt-4">
            <Button variant="outline" onClick={() => setDeleteProduct(null)}>{t('buttons.cancel', { ns: 'common' })}</Button>
            <Button variant="destructive" onClick={() => { if (deleteProduct) deleteMutation.mutate(deleteProduct.id) }} disabled={deleteMutation.isPending}>{t('buttons.delete', { ns: 'common' })}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}
